import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API, auth, inputCls } from '../finance/shared';
import { EmptyState } from '../../../components/dashboard/TodaySchedule';
import Icon from '../../../components/dashboard/DashboardIcons';
import { normalizeWhatsAppNumber, buildWaMeLink, fillTemplate } from '../../../lib/whatsapp';
import { toast } from '../../../lib/toast';

const fmtDate = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleDateString('ar-SA'); } catch { return iso; } };
const daysUntil = (iso) => { if (!iso) return null; const d = (new Date(iso) - new Date()) / 86400000; return Math.ceil(d); };

// «الإرسال المتتابع»: فلترة مجموعة، ثم إرسال يدوي واحداً بعد الآخر — بلا فتح نوافذ متعددة دفعة واحدة
export const BulkSendTab = () => {
  const [players, setPlayers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ category_id: '', subscription_status: '', near_expiry: false, has_remaining: false, search: '' });
  const [excluded, setExcluded] = useState(new Set());
  const [sequenceIndex, setSequenceIndex] = useState(null); // null = لم يبدأ الإرسال بعد
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [text, setText] = useState('');
  const [phone, setPhone] = useState('');
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/players`, auth()).catch(() => ({ data: [] })),
      axios.get(`${API}/categories`, auth()).catch(() => ({ data: [] })),
      axios.get(`${API}/club-settings`, auth()).catch(() => ({ data: {} })),
      axios.get(`${API}/whatsapp/templates`, auth()).catch(() => ({ data: [] })),
    ]).then(([pRes, cRes, sRes, tRes]) => {
      setPlayers(Array.isArray(pRes.data) ? pRes.data : []);
      setCategories(Array.isArray(cRes.data) ? cRes.data : []);
      setSettings(sRes.data || {});
      setTemplates(tRes.data || []);
      if (tRes.data?.length) setSelectedTemplateId(tRes.data[0].id);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => players.filter((p) => {
    if (filters.category_id && p.category_id !== filters.category_id) return false;
    if (filters.subscription_status && p.subscription_status !== filters.subscription_status) return false;
    if (filters.near_expiry) {
      const d = daysUntil(p.subscription_end);
      if (d === null || d < 0 || d > 7) return false;
    }
    if (filters.has_remaining) {
      const remaining = (p.subscription_amount ?? 0) - (p.subscription_paid_amount ?? 0);
      if (!(remaining > 0)) return false;
    }
    if (filters.search && !(p.name || '').toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (!p.guardian_phone) return false;
    return true;
  }), [players, filters]);

  const recipients = filtered.filter((p) => !excluded.has(p.id));
  const current = sequenceIndex !== null ? recipients[sequenceIndex] : null;
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  useEffect(() => {
    if (!current || !selectedTemplate) return;
    const remaining = Math.max((current.subscription_amount ?? 0) - (current.subscription_paid_amount ?? 0), 0);
    const vars = {
      guardian_name: current.guardian_name || 'ولي الأمر',
      player_name: current.name,
      club_name: settings?.whatsapp_display_name,
      subscription_name: current.subscription_package || '',
      expiry_date: fmtDate(current.subscription_end),
      paid_amount: current.subscription_paid_amount ?? '',
      remaining_amount: remaining || '',
      payment_reference: current.subscription_payment_reference || '',
    };
    let filled = fillTemplate(selectedTemplate.body, vars);
    if (settings?.whatsapp_signature) filled += `\n\n${settings.whatsapp_signature}`;
    setText(filled);
    setPhone(current.guardian_phone || '');
    setOpened(false);
  }, [current?.id, selectedTemplateId]);

  const normalizedPhone = phone ? normalizeWhatsAppNumber(phone, settings?.whatsapp_country_code || '966') : null;

  const logAttempt = async (status) => {
    try {
      await axios.post(`${API}/whatsapp/log`, {
        recipient_type: 'player_guardian', recipient_name: current.guardian_name || current.name,
        recipient_phone_raw: phone, recipient_phone_normalized: normalizedPhone,
        template_id: selectedTemplate?.id, template_key: selectedTemplate?.key, template_name: selectedTemplate?.name,
        final_message: text, status, related_type: 'player', related_id: current.id,
      }, auth());
    } catch { /* لا نعطّل التتابع لو فشل تسجيل السجل */ }
  };

  const openWhatsApp = async () => {
    if (!normalizedPhone) { toast.error('رقم غير صالح — عدّله أو تخطّه'); await logAttempt('failed'); return; }
    window.open(buildWaMeLink(normalizedPhone, text), '_blank', 'noopener,noreferrer');
    await logAttempt('opened');
    setOpened(true);
  };

  const goNext = (statusIfSkipped) => {
    if (statusIfSkipped === 'skipped') logAttempt('skipped');
    setSequenceIndex((i) => (i === null ? 0 : i + 1));
  };

  if (loading) return <div className="h-64 animate-pulse rounded-xl bg-slate-100" />;

  if (sequenceIndex !== null && sequenceIndex >= recipients.length) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="bulk-send-finished">
        <p className="text-lg font-bold text-emerald-700">انتهت القائمة</p>
        <p className="text-sm text-slate-500 mt-1">راجع سجل الرسائل لتأكيد ما تم إرساله فعلياً</p>
        <button onClick={() => { setSequenceIndex(null); setExcluded(new Set()); }} data-testid="bulk-send-restart-btn" className="mt-4 rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white">بدء قائمة جديدة</button>
      </div>
    );
  }

  if (current) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="bulk-send-sequence-panel">
        <p className="mb-3 text-xs font-semibold text-slate-400">المستلم {sequenceIndex + 1} من {recipients.length}</p>
        <div className="rounded-xl bg-slate-50 p-3 mb-3">
          <p className="font-bold text-slate-800" data-testid="bulk-send-current-name">{current.guardian_name || current.name}</p>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} dir="ltr" data-testid="bulk-send-current-phone-input" className={`${inputCls} mt-2`} />
          {!normalizedPhone && <span className="text-xs font-semibold text-red-500">رقم غير صالح</span>}
        </div>
        <select value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)} data-testid="bulk-send-template-select" className={`${inputCls} mb-3`}>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} data-testid="bulk-send-textarea" className={`${inputCls} mb-3`} />
        {!opened ? (
          <div className="flex gap-2">
            <button onClick={openWhatsApp} data-testid="bulk-send-open-whatsapp-btn" className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700">
              <Icon name="whatsapp" className="w-4 h-4" /> فتح واتساب
            </button>
            <button onClick={() => goNext('skipped')} data-testid="bulk-send-skip-btn" className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600">تخطي</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { logAttempt('confirmed_sent'); goNext(); }} data-testid="bulk-send-confirm-btn" className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white">تأكيد الإرسال والتالي</button>
            <button onClick={() => goNext()} data-testid="bulk-send-next-btn" className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600">التالي بدون تأكيد</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bulk-send-tab">
      <div className="rounded-xl border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="bulk-send-filters">
        <select value={filters.category_id} onChange={(e) => setFilters({ ...filters, category_id: e.target.value })} data-testid="bulk-filter-category" className={inputCls}>
          <option value="">كل الفئات / الفرق</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filters.subscription_status} onChange={(e) => setFilters({ ...filters, subscription_status: e.target.value })} data-testid="bulk-filter-status" className={inputCls}>
          <option value="">كل حالات الاشتراك</option>
          <option value="active">نشط</option>
          <option value="expired">منتهٍ</option>
          <option value="pending">قيد الانتظار</option>
        </select>
        <input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="بحث بالاسم..." data-testid="bulk-filter-search" className={inputCls} />
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input type="checkbox" checked={filters.near_expiry} onChange={(e) => setFilters({ ...filters, near_expiry: e.target.checked })} data-testid="bulk-filter-near-expiry" /> قرب الانتهاء (٧ أيام)
          </label>
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input type="checkbox" checked={filters.has_remaining} onChange={(e) => setFilters({ ...filters, has_remaining: e.target.checked })} data-testid="bulk-filter-has-remaining" /> يوجد مبلغ متبقٍ
          </label>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="bulk-send-recipients-list">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <span className="text-sm font-bold text-slate-800">المستلمون ({recipients.length})</span>
          <button
            disabled={recipients.length === 0}
            onClick={() => setSequenceIndex(0)}
            data-testid="bulk-send-start-btn"
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-40"
          >
            بدء الإرسال المتتابع
          </button>
        </div>
        {filtered.length === 0 ? (
          <div className="p-6"><EmptyState icon="users" text="لا يوجد مستلمون مطابقون للفلاتر" testId="bulk-send-empty" /></div>
        ) : (
          <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-50">
            {filtered.map((p) => (
              <label key={p.id} className="flex items-center justify-between px-4 py-2.5 cursor-pointer" data-testid="bulk-send-recipient-row">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={!excluded.has(p.id)} onChange={(e) => {
                    const next = new Set(excluded);
                    if (e.target.checked) next.delete(p.id); else next.add(p.id);
                    setExcluded(next);
                  }} data-testid="bulk-send-exclude-checkbox" />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{p.guardian_name || p.name}</p>
                    <p className="text-xs text-slate-400" dir="ltr">{p.guardian_phone}</p>
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
